import {
  AppShell,
  Burger,
  Button,
  Divider,
  Group,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  IconBox,
  IconBuildingCommunity,
  IconBuildingStore,
  IconGauge,
  IconLogout,
  IconPackage,
  IconPlugConnected,
  IconReceipt2,
  IconTruckDelivery,
  IconSettings,
  IconTags,
  IconUserCircle,
  IconSpy,
  IconUsers,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth";
import { endSession } from "@/lib/session";
import { ViewTransition } from "@/components/ViewTransition";
import { useAccess } from "@/hooks/useAccess";
import { PAGE_AREAS, ROLE_LABELS } from "@/lib/permissions";
import { getBrand } from "@/api/tenant";
import { AccountModal } from "@/components/AccountModal";
import classes from "./AppLayout.module.css";

const navItems = [
  { label: "Dashboard", to: "/", icon: IconGauge },
  { label: "Kategoriler", to: "/categories", icon: IconTags },
  { label: "Ürünler", to: "/products", icon: IconBox },
  { label: "Şubeler", to: "/branches", icon: IconBuildingStore },
  { label: "Entegrasyonlar", to: "/connections", icon: IconPlugConnected },
  { label: "Toplu İşlemler", to: "/bulk", icon: IconReceipt2 },
  { label: "İkmal Talepleri", to: "/supply", icon: IconTruckDelivery },
  { label: "İkmal Kalemleri", to: "/supply/items", icon: IconPackage },
  { label: "Ayarlar", to: "/settings", icon: IconSettings },
  { label: "Kullanıcılar", to: "/users", icon: IconUsers },
];

const adminItems = [
  { label: "Partnerler", to: "/admin/tenants", icon: IconBuildingCommunity },
];

function NavbarLink({ icon: Icon, label, active, onClick }) {
  return (
    <Tooltip label={label} position="right" transitionProps={{ duration: 0 }}>
      <UnstyledButton
        className={classes.link}
        data-active={active || undefined}
        aria-current={active ? "page" : undefined}
        aria-label={label}
        onClick={onClick}
      >
        <Icon size={20} stroke={1.5} />
        <span className={classes.linkLabel}>{label}</span>
      </UnstyledButton>
    </Tooltip>
  );
}

export default function AppLayout() {
  const [opened, { toggle, close }] = useDisclosure();
  const [accountOpened, { open: openAccount, close: closeAccount }] =
    useDisclosure(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, impersonation, stopImpersonation } = useAuthStore();
  const { canAny } = useAccess();

  // A superadmin without impersonation has no tenant scope — tenant pages would all
  // 403, so only the admin section is shown until they "act as" a partner.
  const isPlainSuperadmin = user?.role === "superadmin" && !impersonation;

  // Menü, kullanıcının gerçekten girebileceği sayfalara daralır: yetkisi olmayan bir sayfayı
  // görüp açması, sonra her işlemde 403 yemesi kötü bir deneyim. Aynı liste App.jsx'teki
  // RequirePermission ile aynı kaynaktan (PAGE_AREAS) besleniyor.
  const allowedNavItems = navItems.filter(
    (item) => Boolean(PAGE_AREAS[item.to]) && canAny(PAGE_AREAS[item.to]),
  );
  const items = isPlainSuperadmin ? adminItems : allowedNavItems;

  // startsWith tek başına yetmiyor: '/supply/items' açıkken '/supply' de eşleşiyor ve iki öğe
  // birden yanıyor. En uzun eşleşme kazanıyor; menüde olmayan alt yollar (ör.
  // connections/:id/mappings) yine üst öğeyi aktif tutuyor.
  const activeTo = items.reduce(
    (best, item) =>
      item.to !== "/" &&
      location.pathname.startsWith(item.to) &&
      item.to.length > best.length
        ? item.to
        : best,
    "",
  );

  // Brand shown in the panel = the partner being managed (name + logo); plain superadmin
  // has no tenant scope, so it falls back to the Noxus product brand.
  //
  // Kare logo önce geliyor: buradaki üç kullanımın hepsi 26-34px (mobil başlık, sol üst, alttaki
  // avatar), yani yazılı bir marka logosunun okunmadığı boyutlar.
  const { data: brand } = useQuery({
    queryKey: ["brand"],
    queryFn: getBrand,
    enabled: !isPlainSuperadmin,
  });
  const brandName = isPlainSuperadmin
    ? "Noxus"
    : (brand?.name ?? impersonation?.tenant?.name ?? "Noxus");
  const brandLogo =
    (!isPlainSuperadmin && (brand?.markUrl || brand?.logoUrl)) ||
    "/noxus-emblem.svg";

  // Etiket KULLANICIYI tanıtıyor, markayı değil: marka adı zaten sol üstte logosuyla duruyordu
  // ve hesap satırında onu tekrar etmek "hangi hesapla girmişim" sorusunu cevapsız bırakıyordu.
  const accountLabel = isPlainSuperadmin
    ? `${user?.fullName ?? "Noxus"} - Süper Admin`
    : `${user?.fullName ?? brandName} - ${ROLE_LABELS[user?.role] ?? "Yönetici"}`;

  const go = (to) => {
    navigate(to);
    close();
  };

  const exitImpersonation = () => {
    stopImpersonation();
    navigate("/admin/tenants");
  };

  return (
    <AppShell
      header={{ height: { base: 56, sm: 0 } }}
      navbar={{
        width: { base: 270, sm: 80 },
        breakpoint: "sm",
        collapsed: { desktop: false, mobile: !opened },
      }}
      padding="lg"
    >
      <AppShell.Header hiddenFrom="sm">
        <Group h="100%" px="md" gap="sm">
          <Burger
            opened={opened}
            onClick={toggle}
            size="sm"
            aria-label="Menü"
          />
          <img
            src={brandLogo}
            alt={brandName}
            width={26}
            height={26}
            style={{ objectFit: "contain" }}
          />
          <Text fw={700} size="sm" truncate>
            {brandName}
          </Text>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        <nav className={classes.navbar}>
          <UnstyledButton className={classes.brand} onClick={() => go("/")}>
            <img src={brandLogo} alt="" className={classes.brandLogo} />
            <div className={classes.brandText}>
              <Text fw={700} size="sm" lh={1.1} truncate>
                {brandName}
              </Text>
              <Text size="xs" c="dimmed">
                Panel
              </Text>
            </div>
          </UnstyledButton>

          <Divider mb="xs" className={classes.navDivider} />

          <ScrollArea
            className={classes.navMain}
            style={{ flex: 1 }}
            type="never"
          >
            <Stack justify="center" gap={0} h="100%">
              {items.map((item) => {
                const active =
                  item.to === "/"
                    ? location.pathname === "/"
                    : item.to === activeTo;
                return (
                  <NavbarLink
                    key={item.to}
                    icon={item.icon}
                    label={item.label}
                    active={active}
                    onClick={() => go(item.to)}
                  />
                );
              })}
            </Stack>
          </ScrollArea>

          <Stack justify="center" gap={0} className={classes.navbarFooter}>
            <NavbarLink
              icon={IconUserCircle}
              label={accountLabel}
              onClick={openAccount}
            />
            <NavbarLink icon={IconLogout} label="Çıkış" onClick={endSession} />
          </Stack>
        </nav>
      </AppShell.Navbar>

      <AppShell.Main>
        {impersonation ? (
          <Group
            justify="space-between"
            mb="lg"
            p="sm"
            style={{
              background: "var(--mantine-color-orange-0)",
              border: "1px solid var(--mantine-color-orange-3)",
              borderRadius: "var(--mantine-radius-md)",
            }}
          >
            <Group gap="xs">
              <IconSpy size={18} color="var(--mantine-color-orange-7)" />
              <Text size="sm" fw={500}>
                {impersonation.tenant.name} partneri olarak geziyorsun
              </Text>
            </Group>
            <Button
              size="compact-sm"
              variant="light"
              color="orange"
              onClick={exitImpersonation}
            >
              Süper Admin&apos;e Dön
            </Button>
          </Group>
        ) : null}
        <ViewTransition transitionKey={location.pathname}>
          <Outlet />
        </ViewTransition>
      </AppShell.Main>

      <AccountModal opened={accountOpened} onClose={closeAccount} />
    </AppShell>
  );
}
